/**
 * 投稿の色分けルール (ColorRule) v2 移植 (Sprint BG-DataSettings 6+7、GAP-A4)。
 *
 * 旧 `old/packages/shared-types/src/colorRules.ts` を v2 風に再構成:
 *   - 旧 `ColorRule = ColorRuleValue | ColorRuleQuadrant` をそのまま踏襲
 *   - 旧 `MODERN_COLOR_PALETTE` (8 色) を踏襲
 *   - `isValidColorRuleExpr` / `matchValueExpr` / `defaultColorRule` /
 *     `defaultQuadrantColorRule` / `syncColorRuleBucketsFromField` /
 *     `evaluateColorRule` / `resolveFieldValue` をすべて移植
 *
 * v2 での差分:
 *   - `defaultColorRule` の引数を `mode: DistrictMode` ではなく `fieldKey: string` 取りに
 *     変更 (旧の mode 固有 hardcode `bousai`/`kids-bousai`/`walker`/`ecw` を排除し、
 *     v2 では mode-template 由来の dataSettings.postFields[] から fieldKey + options で組み立てる方針)。
 *     mode 固有 default は M5 displayRules / mode-template fields 側で別途定義。
 *   - shared-types 純粋: React import 等は無し、Post / Record<string, unknown> どちらでも評価可能。
 *
 * 用途:
 *   - admin: DistrictColorRulePanel で buckets / cells を編集、Firestore に保存
 *   - user: MapView の marker 色付け + MapLegend の凡例自動生成
 */
/* ===== 色パレット (旧 MODERN_COLOR_PALETTE 完全踏襲) ===== */
/**
 * 旧 v1 と同じ 8 色 modern palette。admin の DistrictColorRulePanel で
 * 新規 bucket / cell を追加した際に「未使用の色」を自動で割り当てる base。
 *
 * 旧 `MODERN_COLOR_PALETTE` (old/packages/shared-types/src/colorRules.ts) の値を
 * そのまま転記。色彩順序 (HSL 配置) も維持して、admin が連続追加した際に近い色が
 * 連続しないようにする。
 */
export const MODERN_COLOR_PALETTE = [
    '#3B82F6', // blue-500
    '#EF4444', // red-500
    '#10B981', // emerald-500
    '#F59E0B', // amber-500
    '#8B5CF6', // violet-500
    '#EC4899', // pink-500
    '#14B8A6', // teal-500
    '#F97316', // orange-500
];
/* ===== 可読性 helper ===== */
/**
 * 背景色 (#RGB / #RRGGBB) の上に載せる文字色を返す (白 or 黒に近いダークグレー)。
 *
 * 色チップ (感情 / 分類 / 段階スケール等) は admin や master が任意の色を設定できるため、
 * 文字色を白に固定すると黄色系・水色系のチップが読めなくなる。知覚輝度 (ITU-R BT.601) で
 * 明るい背景と判定したときだけ濃い文字色に切り替える。
 *
 * 閾値 0.6 は画面間で判定を揃えるための値 (旧 admin のスコアバッジと同値だった)。
 */
export function contrastTextColor(background) {
    const hex = (background ?? '').trim().replace(/^#/, '');
    const full = hex.length === 3
        ? hex
            .split('')
            .map((c) => c + c)
            .join('')
        : hex;
    if (!/^[0-9a-f]{6}$/i.test(full))
        return '#ffffff';
    const n = parseInt(full, 16);
    const r = (n >> 16) & 0xff;
    const g = (n >> 8) & 0xff;
    const b = n & 0xff;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? '#1f2937' : '#ffffff';
}
/* ===== 評価式 helpers (旧 matchValueExpr / resolveFieldValue 踏襲) ===== */
/**
 * 値判定式の評価 (旧 colorRules.ts L16-69 完全踏襲)。
 *   - 空文字 → 常に true
 *   - 数値不等式 (==, !=, >=, <=, >, <) + 数値 → 数値比較
 *   - `==XXX` / `!=XXX` (XXX が数字以外) → 文字列等価 / 不等価
 *   - カンマ区切り → リスト中の任意要素と等価
 *   - 単一数値 → 等価判定
 *   - プレーン文字列 → 文字列等価
 */
export function matchValueExpr(expr, value) {
    const s = (expr ?? '').trim();
    if (!s)
        return true;
    // 数値不等式
    const m = s.match(/^(==|!=|>=|<=|>|<)\s*(-?\d+(?:\.\d+)?)$/);
    if (m) {
        const op = m[1];
        const n = Number(m[2]);
        const v = Number(value);
        if (!Number.isFinite(v))
            return op === '!=';
        switch (op) {
            case '>':
                return v > n;
            case '<':
                return v < n;
            case '>=':
                return v >= n;
            case '<=':
                return v <= n;
            case '==':
                return v === n;
            case '!=':
                return v !== n;
        }
    }
    // 文字列等価 / 不等価 (数値不等式に該当しなかった `==`/`!=` を救う)
    const strM = s.match(/^(==|!=)\s*(.+)$/);
    if (strM) {
        const op = strM[1] ?? '';
        const target = (strM[2] ?? '').trim();
        const equal = String(value) === target;
        return op === '==' ? equal : !equal;
    }
    // カンマ区切りリスト
    if (s.includes(',')) {
        const parts = s
            .split(',')
            .map((t) => t.trim())
            .filter((t) => t.length > 0);
        if (parts.length === 0)
            return false;
        const allNumeric = parts.every((p) => /^-?\d+(\.\d+)?$/.test(p));
        if (allNumeric) {
            const v = Number(value);
            if (!Number.isFinite(v))
                return false;
            return parts.map(Number).includes(v);
        }
        return parts.includes(String(value));
    }
    // 単一の数値 → 等価
    if (/^-?\d+(\.\d+)?$/.test(s)) {
        const v = Number(value);
        return Number.isFinite(v) && v === Number(s);
    }
    return String(value) === s;
}
/** 評価式の構文妥当性 (admin の onBlur バリデーション用、旧と同 logic)。 */
export function isValidColorRuleExpr(expr) {
    const s = (expr ?? '').trim();
    if (!s)
        return true;
    if (/^(==|!=|>=|<=|>|<)\s*-?\d+(\.\d+)?$/.test(s))
        return true;
    if (/^-?\d+(\.\d+)?$/.test(s))
        return true;
    if (s.includes(',')) {
        const parts = s
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean);
        return parts.length > 0;
    }
    return true;
}
/**
 * フィールドキー文字列から値を取得 (dotted path 対応、旧 colorRules.ts L179-197 踏襲)。
 *
 * 解決順:
 *   1. dotted path (`extraFields.HappinessScore` 等) → そのパスを辿る
 *   2. それ以外: `obj.extraFields[key]` を参照
 *
 * 旧 docs/40 設計を踏襲: v2 でも post の主要動的 field は extraFields に一本化。
 */
export function resolveFieldValue(obj, key) {
    if (!key)
        return undefined;
    if (key.includes('.')) {
        const parts = key.split('.');
        let cur = obj;
        for (const p of parts) {
            if (cur && typeof cur === 'object' && p in cur) {
                cur = cur[p];
            }
            else {
                return undefined;
            }
        }
        return cur;
    }
    const extra = obj?.extraFields;
    if (extra && typeof extra === 'object' && key in extra) {
        return extra[key];
    }
    // v2 拡張: top-level にもある場合は fallback で読む (Post.title 等の主要 field を colorRule で使えるよう)
    if (key in obj) {
        return obj[key];
    }
    return undefined;
}
/* ===== Default / Quadrant Default (v2 風 API) ===== */
/**
 * value 形式の最小 default ColorRule。
 *
 * v2: 旧の `defaultColorRule(mode)` ではなく `defaultColorRule(fieldKey)` 取り。
 * admin が初めて ColorRule を有効化したときの「空 bucket 配列 + fieldKey のみ」状態。
 * options を後で追加する場合は `syncColorRuleBucketsFromField` で field 駆動に置換可能。
 */
export function defaultColorRule(fieldKey) {
    return {
        kind: 'value',
        fieldKey,
        buckets: [],
        fallbackColor: '#9CA3AF',
        fallbackLabel: '—',
    };
}
/**
 * quadrant 形式の最小 default ColorRule。
 *
 * 旧 v1 の `Happy & Healthy` 等 4 象限 (ECW) を踏襲した「2 軸正負 4 cell」default。
 * xKey / yKey は admin が指定 (postFields の rating-scale 系 2 つを選ぶ想定)。
 */
export function defaultQuadrantColorRule(xKey, yKey) {
    return {
        kind: 'quadrant',
        xKey,
        yKey,
        cells: [
            { label: '+ +', xExpr: '>0', yExpr: '>0', color: '#EC4899' },
            { label: '+ -', xExpr: '>0', yExpr: '<0', color: '#8B5CF6' },
            { label: '- +', xExpr: '<0', yExpr: '>0', color: '#14B8A6' },
            { label: '- -', xExpr: '<0', yExpr: '<0', color: '#3B82F6' },
        ],
        fallbackColor: '#9E9E9E',
        fallbackLabel: 'ニュートラル',
    };
}
/* ===== Sync from FieldDef (旧 syncColorRuleBucketsFromField 踏襲) ===== */
/**
 * `FieldDef.options` の `{value,label}` ペアから ColorRule.buckets を生成・同期する
 * (旧 colorRules.ts L216-263 完全踏襲)。
 *
 * - 既存 bucket は value (`==XXX` の XXX 部分) を key に color / icon / 既存 label を引き継ぐ
 * - 新規 value は palette から自動採色
 * - options から消えた value の bucket は除去
 * - 戻り値は「提案」: admin が必ず保存ボタンを押す前提 (旧と同 UX)
 */
export function syncColorRuleBucketsFromField(field, current, paletteColors = MODERN_COLOR_PALETTE) {
    const existingBucketByValue = new Map();
    if (current && current.kind === 'value' && current.fieldKey === field.key) {
        for (const b of current.buckets) {
            const m = b.expr.match(/^==\s*(.+)$/);
            const value = m && m[1] !== undefined ? m[1].trim() : b.expr.trim();
            existingBucketByValue.set(value, { color: b.color, label: b.label, icon: b.icon });
        }
    }
    const usedColors = [];
    const buckets = field.options.map((opt, idx) => {
        const existing = existingBucketByValue.get(opt.value);
        let color;
        if (existing) {
            color = existing.color;
        }
        else {
            const fallback = paletteColors[idx % paletteColors.length] ?? '#9CA3AF';
            const candidate = paletteColors.find((c) => !usedColors.includes(c.toLowerCase())) ?? fallback;
            color = candidate;
        }
        usedColors.push(color.toLowerCase());
        const bucket = {
            label: existing?.label ?? opt.label,
            expr: `==${opt.value}`,
            color,
        };
        if (existing?.icon)
            bucket.icon = existing.icon;
        return bucket;
    });
    return {
        kind: 'value',
        fieldKey: field.key,
        buckets,
        fallbackColor: current && current.kind === 'value' ? (current.fallbackColor ?? '#9CA3AF') : '#9CA3AF',
        fallbackLabel: current && current.kind === 'value' ? (current.fallbackLabel ?? '—') : '—',
    };
}
/* ===== evaluateColorRule (旧 evaluateColorRule 踏襲) ===== */
/**
 * ColorRule を post 風オブジェクトに対して評価し、色・ラベル・アイコンを返す。
 * 旧 colorRules.ts L266-291 と同じ semantics。
 *
 * - value: `fieldKey` の値を `resolveFieldValue` で引き、`matchValueExpr` で bucket 走査
 * - quadrant: `xKey` / `yKey` の値を引き、`matchValueExpr` を `xExpr` / `yExpr` 両方に適用
 * - いずれもマッチしなければ `fallbackColor` / `fallbackLabel` (未指定なら `#9CA3AF` / `—`)
 */
export function evaluateColorRule(rule, source) {
    if (rule.kind === 'value') {
        const v = resolveFieldValue(source, rule.fieldKey);
        for (const b of rule.buckets) {
            if (matchValueExpr(b.expr, v)) {
                const out = { color: b.color, label: b.label };
                if (b.icon)
                    out.icon = b.icon;
                return out;
            }
        }
        return {
            color: rule.fallbackColor ?? '#9CA3AF',
            label: rule.fallbackLabel ?? '—',
        };
    }
    // quadrant
    const x = resolveFieldValue(source, rule.xKey);
    const y = resolveFieldValue(source, rule.yKey);
    for (const c of rule.cells) {
        if (matchValueExpr(c.xExpr, x) && matchValueExpr(c.yExpr, y)) {
            const out = { color: c.color, label: c.label };
            if (c.icon)
                out.icon = c.icon;
            return out;
        }
    }
    return {
        color: rule.fallbackColor ?? '#9CA3AF',
        label: rule.fallbackLabel ?? '—',
    };
}
export function colorRuleLegendEntries(rule, includeFallback = true) {
    const entries = rule.kind === 'value'
        ? rule.buckets.map((b) => {
            const e = { color: b.color, label: b.label };
            if (b.icon)
                e.icon = b.icon;
            return e;
        })
        : rule.cells.map((c) => {
            const e = { color: c.color, label: c.label };
            if (c.icon)
                e.icon = c.icon;
            return e;
        });
    if (includeFallback && (rule.fallbackColor || rule.fallbackLabel)) {
        entries.push({
            color: rule.fallbackColor ?? '#9CA3AF',
            label: rule.fallbackLabel ?? '—',
        });
    }
    return entries;
}
/* ===== Safe parser (admin 由来の Firestore 値が unknown 型でも crash しないように) ===== */
function isRecord(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
/**
 * Firestore から読み出した unknown を安全に `ColorRule` に変換。
 *
 * - 入力が null / 非 object / 必須 field 欠落の場合は null を返す
 * - buckets / cells は配列であり各 entry が必須 field (label/expr/color) を持つもののみ採用
 */
export function parseColorRule(raw) {
    if (!isRecord(raw))
        return null;
    if (raw.kind === 'value') {
        const fieldKey = typeof raw.fieldKey === 'string' ? raw.fieldKey : '';
        if (!fieldKey)
            return null;
        const bucketsRaw = Array.isArray(raw.buckets) ? raw.buckets : [];
        const buckets = [];
        for (const b of bucketsRaw) {
            if (!isRecord(b))
                continue;
            const label = typeof b.label === 'string' ? b.label : '';
            const expr = typeof b.expr === 'string' ? b.expr : '';
            const color = typeof b.color === 'string' ? b.color : '';
            if (!label || !color)
                continue;
            const bucket = { label, expr, color };
            if (typeof b.icon === 'string' && b.icon)
                bucket.icon = b.icon;
            buckets.push(bucket);
        }
        const rule = { kind: 'value', fieldKey, buckets };
        if (typeof raw.fallbackColor === 'string')
            rule.fallbackColor = raw.fallbackColor;
        if (typeof raw.fallbackLabel === 'string')
            rule.fallbackLabel = raw.fallbackLabel;
        return rule;
    }
    if (raw.kind === 'quadrant') {
        const xKey = typeof raw.xKey === 'string' ? raw.xKey : '';
        const yKey = typeof raw.yKey === 'string' ? raw.yKey : '';
        if (!xKey || !yKey)
            return null;
        const cellsRaw = Array.isArray(raw.cells) ? raw.cells : [];
        const cells = [];
        for (const c of cellsRaw) {
            if (!isRecord(c))
                continue;
            const label = typeof c.label === 'string' ? c.label : '';
            const xExpr = typeof c.xExpr === 'string' ? c.xExpr : '';
            const yExpr = typeof c.yExpr === 'string' ? c.yExpr : '';
            const color = typeof c.color === 'string' ? c.color : '';
            if (!label || !color)
                continue;
            const cell = { label, xExpr, yExpr, color };
            if (typeof c.icon === 'string' && c.icon)
                cell.icon = c.icon;
            cells.push(cell);
        }
        const rule = { kind: 'quadrant', xKey, yKey, cells };
        if (typeof raw.fallbackColor === 'string')
            rule.fallbackColor = raw.fallbackColor;
        if (typeof raw.fallbackLabel === 'string')
            rule.fallbackLabel = raw.fallbackLabel;
        return rule;
    }
    return null;
}
//# sourceMappingURL=colorRule.js.map